/**
 * Ballotr RSS Sources (v1)
 *
 * NOTE:
 * - `name` is what we store in `campaigns.source` and what the UI filters on.
 * - Keep `name` values stable once live, or you’ll fragment filters.
 */

export type RssBias = 'left' | 'center' | 'right';

export interface RssSource {
  /** Human-friendly stable name (stored in DB as campaigns.source) */
  name: string;
  /** RSS URL used by the ingestion job */
  feedUrl: string;
  /** Rough editorial leaning (used only for internal balancing, not shown to users by default) */
  bias: RssBias;
  /** Optional per-source daily cap override (default = 10) */
  maxPerDay?: number;
}

/**
 * Locked list: 24 sources.
 * Cap (enforced in ingestion): 10 items/source/day.
 */
export const RSS_SOURCES: readonly RssSource[] = [
  // Center / wire
  { name: 'PBS', feedUrl: 'https://www.pbs.org/newshour/feeds/rss/politics', bias: 'center' },
  { name: 'NPR', feedUrl: 'https://www.npr.org/rss/rss.php?id=1014', bias: 'center' },
  { name: 'Axios', feedUrl: 'https://www.axios.com/feeds/feed.rss', bias: 'center' },
  { name: 'The Hill', feedUrl: 'https://thehill.com/rss/syndicator/19110', bias: 'center' },
  { name: 'ABC', feedUrl: 'https://feeds.abcnews.com/abcnews/politicsheadlines', bias: 'center' },
  { name: 'CBS', feedUrl: 'https://www.cbsnews.com/latest/rss/politics', bias: 'center' },
  { name: 'Ballotpedia', feedUrl: 'https://news.ballotpedia.org/feed/', bias: 'center' },
  { name: "BBC News", feedUrl: "https://feeds.bbci.co.uk/news/world/us_and_canada/rss.xml", bias: 'center', maxPerDay: 8 },
  { name: 'Al Jazeera', feedUrl: 'https://www.aljazeera.com/xml/rss/all.xml', bias: 'center', maxPerDay: 8 },

  // Government (primary sources)
  { name: 'Federal Register - Executive Orders', feedUrl: 'https://www.federalregister.gov/executive-orders.rss', bias: 'center', maxPerDay: 2 },
  { name: 'GovInfo - Congressional Bills', feedUrl: 'https://www.govinfo.gov/rss/bills.xml', bias: 'center', maxPerDay: 2 },

  // Left
  { name: 'CNN', feedUrl: 'https://rss.cnn.com/rss/cnn_allpolitics.rss', bias: 'left' },
  { name: 'NBC', feedUrl: 'https://feeds.nbcnews.com/nbcnews/public/politics', bias: 'left' },
  { name: 'Guardian', feedUrl: 'https://www.theguardian.com/us-news/us-politics/rss', bias: 'left' },
  { name: 'Politico', feedUrl: 'https://www.politico.com/rss/politics08.xml', bias: 'left' },
  { name: 'Vox', feedUrl: 'https://www.vox.com/rss/politics/index.xml', bias: 'left' },
  { name: 'New York Times', feedUrl: 'https://rss.nytimes.com/services/xml/rss/nyt/Politics.xml', bias: 'left', maxPerDay: 8},

  // Right
  { name: 'Breitbart', feedUrl: 'https://www.breitbart.com/politics/feed/', bias: 'right', maxPerDay: 6},
  { name: 'Fox News', feedUrl: 'https://feeds.foxnews.com/foxnews/politics', bias: 'right' },
  { name: 'National Review', feedUrl: 'https://www.nationalreview.com/feed', bias: 'right' },
  { name: 'Washington Times', feedUrl: 'https://www.washingtontimes.com/rss/headlines/news/politics/', bias: 'right' },
  { name: 'RealClearPolitics', feedUrl: 'https://www.realclearpolitics.com/index.xml', bias: 'right' },
  { name: 'Reason', feedUrl: 'https://reason.com/feed/', bias: 'right' },
] as const;

/** UI-friendly sorted list of source names for filters */
export const RSS_SOURCE_NAMES: readonly string[] = [...RSS_SOURCES]
  .map(s => s.name)
  .sort((a, b) => a.localeCompare(b));
